import moment from 'moment-timezone';
import React from 'react';
import { FormattedMessage, WrappedComponentProps, injectIntl, IntlProvider } from 'react-intl';

import { DEFAULT_DATE_FORMAT, DEFAULT_TIMEZONE, DMY_DATE_FORMAT } from 'core/config/global';

import { SupportedLocale } from './locales';
import * as Types from './types.d';

// This module does two things:
// 1. It provides an abstraction for how localization is done, decoupling the app from the
//    specific implementation, e.g. if we swapped out react-intl for something else, we'd just
//    need to change this file.
// 2. It enforces types, i.e. it will cause compilation to fail if you use a message ID not present
//    in the l10n json file.

export type L10nMessages = Types.L10nMessages;

export type MessageValue = Types.MessageValue;

export type L10nContext = Types.L10nContext;

export type L10nProviderProps = Types.L10nConsumerProps;

export const L10nProvider: React.SFC<Types.L10nProviderProps> = (props) => {
    return <IntlProvider textComponent={React.Fragment} {...props} />;
};

export type LocalizedMessageProps = Types.LocalizedMessageProps;

export const LocalizedMessage: React.SFC<Types.LocalizedMessageProps> = (props) => {
    return <FormattedMessage {...props} />;
};

const getDateFormatForLocale = (locale: string) => {
    switch (locale) {
        case SupportedLocale.frCA:
        case SupportedLocale.frFR:
            return DMY_DATE_FORMAT;
        default:
            return DEFAULT_DATE_FORMAT;
    }
};

export const LocalizedDate = injectIntl((props: Types.LocalizedDateProps & WrappedComponentProps) => {
    return (
        <>
            {moment
                .tz(props.date, props.timezone || DEFAULT_TIMEZONE)
                .format(getDateFormatForLocale(props.intl.locale))}
        </>
    );
});

export type L10nConsumerProps = Types.L10nConsumerProps;

export const L10nConsumer = injectIntl((props: Types.L10nConsumerProps & WrappedComponentProps) => {
    const l10n: Types.L10nContext = {
        localizeMessage: (id, values) => props.intl.formatMessage({ id }, values),
        localizeDateFormat: () => getDateFormatForLocale(props.intl.locale),
    };
    return props.children(l10n);
});
